// client/src/App.tsx (FULL UPDATED FILE)
import React, { useEffect, useState, lazy, Suspense } from 'react';
import { BrowserRouter as Router, Routes, Route, Navigate } from 'react-router-dom';
import { AuthProvider, useAuth } from './context/AuthContext';
import { clearAuthStorage } from './lib/authSession';
import { NotificationProvider } from './context/NotificationContext';
import { ToastProvider } from './context/ToastContext';
import { ConfirmProvider } from './components/ui/ConfirmProvider';
import LoadingState from './components/LoadingState';
import { ForgotPasswordPage } from './pages/ForgotPasswordPage';
import { ResetPasswordPage } from './pages/ResetPasswordPage';
import AppDashboard from './components/AppDashboard';
import { MainLayout } from './layouts/MainLayout';
import { RfqInboxPage } from './features/rfqInbox/RfqInboxPage';
import { PermissionRoute } from './components/PermissionRoute';
import { InboxRedirect, NotFoundPage } from './components/NotFoundPage';
// BUG_REPORTER:BEGIN — temporary feature, remove per docs/BUG_REPORTER.md
import { BugReportsPage } from './features/bugReport/BugReportsPage';
// BUG_REPORTER:END
import { UsersPage } from './pages/UsersPage';
import { SettingsPage } from './pages/SettingsPage';
import { ReportsPage } from './pages/ReportsPage';
import { QuotationsPage } from './pages/QuotationsPage';
import { QuotationDetailPage } from './pages/QuotationDetailPage';
import { ClientsPage } from './pages/ClientsPage';
import { ConfirmedOrdersPage } from './pages/ConfirmedOrdersPage';
import { ConfirmedOrderDetailPage } from './pages/ConfirmedOrderDetailPage';
import { ClientInvoicePage } from './pages/ClientInvoicePage';
import { ClientInvoicesPage } from './pages/ClientInvoicesPage';
import { CreditNotesPage } from './pages/CreditNotesPage';
import { CreditNoteDetailPage } from './pages/CreditNoteDetailPage';
import { CreditNoteRegisterPage } from './pages/CreditNoteRegisterPage';
import { DebitNoteDetailPage } from './pages/DebitNoteDetailPage';
import { VendorPurchaseOrdersPage } from './pages/VendorPurchaseOrdersPage';
import { VendorPurchaseOrderDetailPage } from './pages/VendorPurchaseOrderDetailPage';

// ✅ Vendors
import { VendorsPage } from './pages/VendorsPage';
import { VendorDetailPage } from './pages/VendorDetailPage';

// ✅ Price List
import { PriceListPage } from './pages/PriceListPage';
import ClientPriceListsPage from './pages/ClientPriceListsPage';
import ClientPriceListDetailPage from './pages/ClientPriceListDetailPage';
import ClientPriceListResponsePage from './pages/ClientPriceListResponsePage';

// ✅ Currency Conversion page
import { CurrencyConversionPage } from './pages/CurrencyConversionPage';

// ✅ Vendor Invoices
import { VendorInvoicesPage } from './pages/VendorInvoicesPage';
import { AccountsPage } from './pages/AccountsPage';
import { AccountsPayableDetailPage } from './pages/AccountsPayableDetailPage';
import { AccountsPayablePaymentVoucherPage } from './pages/AccountsPayablePaymentVoucherPage';
import { AccountsPayableVouchersPage } from './pages/AccountsPayableVouchersPage';
import { AccountsSupplierDetailPage } from './pages/AccountsSupplierDetailPage';
import { SupplierStatementPage } from './pages/SupplierStatementPage';
import { AccountsPayableReportsPage } from './pages/AccountsPayableReportsPage';
import { PaymentRunsPage } from './pages/PaymentRunsPage';
import { PaymentRunPage } from './pages/PaymentRunPage';

// ✅ Inventory (NEW)
import InventoryPage from './pages/InventoryPage';

// ✅ Master Catalogue
import MasterCataloguePage from './pages/MasterCataloguePage';
import ItemGroupsPage from './pages/ItemGroupsPage';

// ✅ Review — catalogue additions + packing alerts
import ReviewPage from './pages/ReviewPage';

import './App.css';

// Lazy-loaded so framer-motion (used only by the redesigned login) stays out of
// the main app bundle that authenticated pages load.
const Login = lazy(() => import('./components/Login'));

// Protected Route Component
const ProtectedRoute: React.FC<{ children: React.ReactNode }> = ({ children }) => {
  const { isAuthenticated, isLoading } = useAuth();

  if (isLoading) {
    return <div className="min-h-screen"><LoadingState /></div>;
  }

  // ✅ Important: replace prevents back-button returning into app after logout
  return isAuthenticated ? <>{children}</> : <Navigate to="/login" replace />;
};

// Public Route Component (redirect if already authenticated)
const PublicRoute: React.FC<{ children: React.ReactNode }> = ({ children }) => {
  const { isAuthenticated, isLoading } = useAuth();

  if (isLoading) {
    return <div className="min-h-screen"><LoadingState /></div>;
  }

  // ✅ Important: replace avoids history weirdness
  return !isAuthenticated ? <>{children}</> : <Navigate to="/app" replace />;
};

function AppRoutes() {
  return (
    <Suspense fallback={<div className="min-h-screen"><LoadingState /></div>}>
    <Routes>
      <Route path="/" element={<Navigate to="/login" replace />} />

      <Route
        path="/login"
        element={
          <PublicRoute>
            <Login />
          </PublicRoute>
        }
      />

      <Route
        path="/forgot-password"
        element={
          <PublicRoute>
            <ForgotPasswordPage />
          </PublicRoute>
        }
      />
      <Route path="/reset-password" element={<ResetPasswordPage />} />

      {/* Section A — Public client-facing price list response form (no auth) */}
      <Route path="/respond/:id" element={<ClientPriceListResponsePage />} />

      {/* BUG_REPORTER:BEGIN — temporary feature, remove per docs/BUG_REPORTER.md */}
      {/* No PermissionRoute, deliberately: every authenticated user may see the
          bugs they reported. Adding a `bug_reports` screen to RBAC would mean
          backfilling every custom Role document in Mongo, and un-backfilling it
          on removal. The service still scopes rows to own + everyone's open. */}
      <Route
        path="/bug-reports"
        element={
          <ProtectedRoute>
            <MainLayout>
              <BugReportsPage />
            </MainLayout>
          </ProtectedRoute>
        }
      />
      {/* BUG_REPORTER:END */}

      <Route
        path="/app"
        element={
          <ProtectedRoute>
            <MainLayout>
              <AppDashboard />
            </MainLayout>
          </ProtectedRoute>
        }
      />

      <Route
        path="/rfq"
        element={
          <ProtectedRoute>
            <PermissionRoute requiredPermission={{ screen: 'quotations', type: 'view' }}>
              <MainLayout>
                <RfqInboxPage />
              </MainLayout>
            </PermissionRoute>
          </ProtectedRoute>
        }
      />

      <Route
        path="/quotations"
        element={
          <ProtectedRoute>
            <PermissionRoute requiredPermission={{ screen: 'quotations', type: 'view' }}>
              <MainLayout>
                <QuotationsPage />
              </MainLayout>
            </PermissionRoute>
          </ProtectedRoute>
        }
      />

      <Route
        path="/quotations/:id"
        element={
          <ProtectedRoute>
            <PermissionRoute requiredPermission={{ screen: 'quotations', type: 'view' }}>
              <MainLayout>
                <QuotationDetailPage />
              </MainLayout>
            </PermissionRoute>
          </ProtectedRoute>
        }
      />

      <Route
        path="/confirmed-orders"
        element={
          <ProtectedRoute>
            <PermissionRoute requiredPermission={{ screen: 'confirmed_orders', type: 'view' }}>
              <MainLayout>
                <ConfirmedOrdersPage />
              </MainLayout>
            </PermissionRoute>
          </ProtectedRoute>
        }
      />

      <Route
        path="/confirmed-orders/:id"
        element={
          <ProtectedRoute>
            <PermissionRoute requiredPermission={{ screen: 'confirmed_orders', type: 'view' }}>
              <MainLayout>
                <ConfirmedOrderDetailPage />
              </MainLayout>
            </PermissionRoute>
          </ProtectedRoute>
        }
      />

      <Route
        path="/client-invoices"
        element={
          <ProtectedRoute>
            <PermissionRoute requiredPermission={{ screen: 'invoices', type: 'view' }}>
              <MainLayout>
                <ClientInvoicesPage />
              </MainLayout>
            </PermissionRoute>
          </ProtectedRoute>
        }
      />

      <Route
        path="/client-invoices/:id"
        element={
          <ProtectedRoute>
            <PermissionRoute requiredPermission={{ screen: 'invoices', type: 'view' }}>
              <MainLayout>
                <ClientInvoicePage />
              </MainLayout>
            </PermissionRoute>
          </ProtectedRoute>
        }
      />

      <Route
        path="/credit-notes"
        element={
          <ProtectedRoute>
            <PermissionRoute requiredPermission={{ screen: 'invoices', type: 'view' }}>
              <MainLayout>
                <CreditNotesPage />
              </MainLayout>
            </PermissionRoute>
          </ProtectedRoute>
        }
      />

      {/* Declared before /credit-notes/:id for the reader's benefit — React Router
          ranks a static segment above a dynamic one regardless of order. */}
      <Route
        path="/credit-notes/register"
        element={
          <ProtectedRoute>
            <PermissionRoute requiredPermission={{ screen: 'invoices', type: 'view' }}>
              <MainLayout>
                <CreditNoteRegisterPage />
              </MainLayout>
            </PermissionRoute>
          </ProtectedRoute>
        }
      />

      {/* D44: vendor returns (DN) live under Credit Notes, Vendor tab. */}
      <Route
        path="/credit-notes/vendor/new"
        element={
          <ProtectedRoute>
            <PermissionRoute requiredPermission={{ screen: 'invoices', type: 'view' }}>
              <MainLayout>
                <DebitNoteDetailPage />
              </MainLayout>
            </PermissionRoute>
          </ProtectedRoute>
        }
      />

      <Route
        path="/credit-notes/vendor/:id"
        element={
          <ProtectedRoute>
            <PermissionRoute requiredPermission={{ screen: 'invoices', type: 'view' }}>
              <MainLayout>
                <DebitNoteDetailPage />
              </MainLayout>
            </PermissionRoute>
          </ProtectedRoute>
        }
      />

      <Route
        path="/credit-notes/:id"
        element={
          <ProtectedRoute>
            <PermissionRoute requiredPermission={{ screen: 'invoices', type: 'view' }}>
              <MainLayout>
                <CreditNoteDetailPage />
              </MainLayout>
            </PermissionRoute>
          </ProtectedRoute>
        }
      />

      <Route
        path="/confirmed-orders/:id/invoice"
        element={
          <ProtectedRoute>
            <PermissionRoute requiredPermission={{ screen: 'invoices', type: 'view' }}>
              <MainLayout>
                <ClientInvoicePage />
              </MainLayout>
            </PermissionRoute>
          </ProtectedRoute>
        }
      />

      <Route
        path="/vendor-purchase-orders"
        element={
          <ProtectedRoute>
            <PermissionRoute requiredPermission={{ screen: 'vendor_purchase_orders', type: 'view' }}>
              <MainLayout>
                <VendorPurchaseOrdersPage />
              </MainLayout>
            </PermissionRoute>
          </ProtectedRoute>
        }
      />

      <Route
        path="/vendor-purchase-orders/:id"
        element={
          <ProtectedRoute>
            <PermissionRoute requiredPermission={{ screen: 'vendor_purchase_orders', type: 'view' }}>
              <MainLayout>
                <VendorPurchaseOrderDetailPage />
              </MainLayout>
            </PermissionRoute>
          </ProtectedRoute>
        }
      />

      {/* ✅ Vendors */}
      <Route
        path="/vendors"
        element={
          <ProtectedRoute>
            <PermissionRoute requiredPermission={{ screen: 'vendors', type: 'view' }}>
              <MainLayout>
                <VendorsPage />
              </MainLayout>
            </PermissionRoute>
          </ProtectedRoute>
        }
      />

      <Route
        path="/vendors/:id"
        element={
          <ProtectedRoute>
            <PermissionRoute requiredPermission={{ screen: 'vendors', type: 'view' }}>
              <MainLayout>
                <VendorDetailPage />
              </MainLayout>
            </PermissionRoute>
          </ProtectedRoute>
        }
      />

      {/* ✅ Price List */}
      <Route
        path="/price-list"
        element={
          <ProtectedRoute>
            <PermissionRoute requiredPermission={{ screen: 'products', type: 'view' }}>
              <MainLayout>
                <PriceListPage />
              </MainLayout>
            </PermissionRoute>
          </ProtectedRoute>
        }
      />

      {/* ✅ Client Price List (from Master Catalog) */}
      <Route
        path="/client-price-lists"
        element={
          <ProtectedRoute>
            <PermissionRoute requiredPermission={{ screen: 'products', type: 'view' }}>
              <MainLayout>
                <ClientPriceListsPage />
              </MainLayout>
            </PermissionRoute>
          </ProtectedRoute>
        }
      />
      <Route
        path="/client-price-lists/:id"
        element={
          <ProtectedRoute>
            <PermissionRoute requiredPermission={{ screen: 'products', type: 'view' }}>
              <MainLayout>
                <ClientPriceListDetailPage />
              </MainLayout>
            </PermissionRoute>
          </ProtectedRoute>
        }
      />

      {/* ✅ Inventory (NEW) */}
      <Route
        path="/inventory"
        element={
          <ProtectedRoute>
            <PermissionRoute requiredPermission={{ screen: 'inventory', type: 'view' }}>
              <MainLayout>
                <InventoryPage key="ship" division="ship" />
              </MainLayout>
            </PermissionRoute>
          </ProtectedRoute>
        }
      />
      <Route
        path="/inventory/pantry"
        element={
          <ProtectedRoute>
            <PermissionRoute requiredPermission={{ screen: 'inventory', type: 'view' }}>
              <MainLayout>
                <InventoryPage key="pantry" division="pantry" />
              </MainLayout>
            </PermissionRoute>
          </ProtectedRoute>
        }
      />

      {/* ✅ Vendor Invoices. `:id?` is ONE route on purpose (lane 5, V5): `/vendor-invoices/<id>` opens that
          invoice on the list page, and the address follows an invoice as it is opened and closed. Two
          routes would remount the page, and the open invoice with it, on every such change. */}
      <Route
        path="/vendor-invoices/:id?"
        element={
          <ProtectedRoute>
            <PermissionRoute requiredPermission={{ screen: 'invoices', type: 'view' }}>
              <MainLayout>
                <VendorInvoicesPage />
              </MainLayout>
            </PermissionRoute>
          </ProtectedRoute>
        }
      />

      <Route
        path="/accounts"
        element={
          <ProtectedRoute>
            <PermissionRoute requiredPermission={{ screen: 'financials', type: 'view' }}>
              <MainLayout>
                <AccountsPage />
              </MainLayout>
            </PermissionRoute>
          </ProtectedRoute>
        }
      />

      {/* D49 phase 2: aging, due schedule and supplier balances. Before "/accounts/:id" so
          "reports" is never read as a payable id. */}
      <Route
        path="/accounts/reports"
        element={
          <ProtectedRoute>
            <PermissionRoute requiredPermission={{ screen: 'financials', type: 'view' }}>
              <MainLayout>
                <AccountsPayableReportsPage />
              </MainLayout>
            </PermissionRoute>
          </ProtectedRoute>
        }
      />

      {/* D49 phase 4: payment runs (a saved list of what to pay, recorded supplier by supplier). Before
          "/accounts/:id" so "payment-runs" is never read as a payable id. */}
      <Route
        path="/accounts/payment-runs"
        element={
          <ProtectedRoute>
            <PermissionRoute requiredPermission={{ screen: 'financials', type: 'view' }}>
              <MainLayout>
                <PaymentRunsPage />
              </MainLayout>
            </PermissionRoute>
          </ProtectedRoute>
        }
      />
      <Route
        path="/accounts/payment-runs/:id"
        element={
          <ProtectedRoute>
            <PermissionRoute requiredPermission={{ screen: 'financials', type: 'view' }}>
              <MainLayout>
                <PaymentRunPage />
              </MainLayout>
            </PermissionRoute>
          </ProtectedRoute>
        }
      />

      <Route
        path="/accounts/:id"
        element={
          <ProtectedRoute>
            <PermissionRoute requiredPermission={{ screen: 'financials', type: 'view' }}>
              <MainLayout>
                <AccountsPayableDetailPage />
              </MainLayout>
            </PermissionRoute>
          </ProtectedRoute>
        }
      />

      <Route
        path="/accounts/payments/:id"
        element={
          <ProtectedRoute>
            <PermissionRoute requiredPermission={{ screen: 'financials', type: 'view' }}>
              <MainLayout>
                <AccountsPayablePaymentVoucherPage />
              </MainLayout>
            </PermissionRoute>
          </ProtectedRoute>
        }
      />

      <Route
        path="/accounts/vouchers"
        element={
          <ProtectedRoute>
            <PermissionRoute requiredPermission={{ screen: 'financials', type: 'view' }}>
              <MainLayout>
                <AccountsPayableVouchersPage />
              </MainLayout>
            </PermissionRoute>
          </ProtectedRoute>
        }
      />

      <Route
        path="/accounts/suppliers/:supplierName"
        element={
          <ProtectedRoute>
            <PermissionRoute requiredPermission={{ screen: 'financials', type: 'view' }}>
              <MainLayout>
                <AccountsSupplierDetailPage />
              </MainLayout>
            </PermissionRoute>
          </ProtectedRoute>
        }
      />

      {/* D49 phase 3: a supplier's statement of account (read-only) */}
      <Route
        path="/accounts/suppliers/:supplierName/statement"
        element={
          <ProtectedRoute>
            <PermissionRoute requiredPermission={{ screen: 'financials', type: 'view' }}>
              <MainLayout>
                <SupplierStatementPage />
              </MainLayout>
            </PermissionRoute>
          </ProtectedRoute>
        }
      />

      {/* ✅ Master Catalogue */}
      <Route
        path="/master-catalogue"
        element={
          <ProtectedRoute>
            <PermissionRoute requiredPermission={{ screen: 'products', type: 'view' }}>
              <MainLayout>
                <MasterCataloguePage />
              </MainLayout>
            </PermissionRoute>
          </ProtectedRoute>
        }
      />

      {/* Legacy master-catalog route (missing the "ue") - redirect to master catalogue */}
      <Route path="/master-catalog" element={<Navigate to="/master-catalogue" replace />} />

      {/* ✅ Assorted item groups — an overlay on the master catalogue, so same permission */}
      <Route
        path="/item-groups"
        element={
          <ProtectedRoute>
            <PermissionRoute requiredPermission={{ screen: 'products', type: 'view' }}>
              <MainLayout>
                <ItemGroupsPage />
              </MainLayout>
            </PermissionRoute>
          </ProtectedRoute>
        }
      />

      {/* ✅ Review — catalogue additions + packing alerts */}
      <Route
        path="/review"
        element={
          <ProtectedRoute>
            <PermissionRoute requiredPermission={{ screen: 'catalogue_review', type: 'view' }}>
              <MainLayout>
                <ReviewPage />
              </MainLayout>
            </PermissionRoute>
          </ProtectedRoute>
        }
      />

      <Route
        path="/accounts"
        element={
          <ProtectedRoute>
            <PermissionRoute requiredPermission={{ screen: 'financials', type: 'view' }}>
              <MainLayout>
                <AccountsPage />
              </MainLayout>
            </PermissionRoute>
          </ProtectedRoute>
        }
      />

      <Route
        path="/users"
        element={
          <ProtectedRoute>
            <PermissionRoute requiredPermission={{ screen: 'users', type: 'view' }}>
              <MainLayout>
                <UsersPage />
              </MainLayout>
            </PermissionRoute>
          </ProtectedRoute>
        }
      />

      <Route
        path="/clients"
        element={
          <ProtectedRoute>
            <PermissionRoute requiredPermission={{ screen: 'clients', type: 'view' }}>
              <MainLayout>
                <ClientsPage />
              </MainLayout>
            </PermissionRoute>
          </ProtectedRoute>
        }
      />

      {/* Legacy roles route - redirect to users page */}
      <Route path="/roles" element={<Navigate to="/users" replace />} />

      {/* Legacy user-management route - redirect to users page */}
      <Route path="/user-management" element={<Navigate to="/users" replace />} />

      <Route
        path="/reports"
        element={
          <ProtectedRoute>
            <PermissionRoute requiredPermission={{ screen: 'reports', type: 'view' }}>
              <MainLayout>
                <ReportsPage />
              </MainLayout>
            </PermissionRoute>
          </ProtectedRoute>
        }
      />

      <Route
        path="/settings"
        element={
          <ProtectedRoute>
            <PermissionRoute requiredPermission={{ screen: 'settings', type: 'view' }}>
              <MainLayout>
                <SettingsPage />
              </MainLayout>
            </PermissionRoute>
          </ProtectedRoute>
        }
      />

      {/* ✅ Currency Conversion */}
      <Route
        path="/currency-conversion"
        element={
          <ProtectedRoute>
            <PermissionRoute requiredPermission={{ screen: 'settings', type: 'view' }}>
              <MainLayout>
                <CurrencyConversionPage />
              </MainLayout>
            </PermissionRoute>
          </ProtectedRoute>
        }
      />

      {/* Legacy admin route - redirect to users page */}
      <Route path="/admin" element={<Navigate to="/users" replace />} />

      {/* Legacy home route - redirect to the app dashboard */}
      <Route path="/home" element={<Navigate to="/app" replace />} />

      {/* D104.27: /inbox is where people type; the inbox itself is /rfq. */}
      <Route path="/inbox" element={<ProtectedRoute><InboxRedirect /></ProtectedRoute>} />

      {/* Last: no URL renders blank. Inside the layout so the nav is still there. */}
      <Route
        path="*"
        element={
          <ProtectedRoute>
            <MainLayout>
              <NotFoundPage />
            </MainLayout>
          </ProtectedRoute>
        }
      />
    </Routes>
    </Suspense>
  );
}

function App() {
  const [sessionExpiredOpen, setSessionExpiredOpen] = useState(false);
  const [sessionExpiredMessage] = useState(
    'Your session has expired. Please log in again to continue.'
  );

  useEffect(() => {
    const handler = (_e: Event) => {
      setSessionExpiredOpen(true);
    };
    window.addEventListener('session-expired', handler as EventListener);
    return () => window.removeEventListener('session-expired', handler as EventListener);
  }, []);

  const handleLoginAgain = () => {
    // One clear path (lib/authSession) — this hand-rolled list covered three
    // key names and missed the rest of the ones clearAuthStorage knows about.
    const { failed } = clearAuthStorage();
    if (failed.length) {
      // Storage refused the removal (disabled/quota/security mode). Say so
      // rather than redirecting as if the session were gone; the login page
      // overwrites the token on success either way.
      window.alert(
        `Could not clear ${failed.length} stored credential key(s) (${failed.join(', ')}). ` +
          'Close this tab after logging in again, or clear site data in the browser.'
      );
    }
    window.location.href = '/login';
  };

  return (
    <AuthProvider>
      <NotificationProvider>
      <ToastProvider>
      <ConfirmProvider>
      <Router>
        <div className="App">
          <AppRoutes />
        </div>
        {sessionExpiredOpen && (
          <div className="fixed inset-0 z-[999] flex items-center justify-center bg-black/30 px-4">
            <div className="w-full max-w-md rounded-xl bg-white p-5 shadow-xl">
              <div className="text-sm font-semibold text-slate-800">Session expired</div>
              <div className="mt-2 text-xs text-slate-600">
                {sessionExpiredMessage}
              </div>
              <div className="mt-4 flex justify-end gap-2">
                <button
                  onClick={handleLoginAgain}
                  className="px-4 py-2 rounded-md bg-primary-500 text-white text-xs font-semibold hover:bg-primary-600"
                >
                  Login again
                </button>
              </div>
            </div>
          </div>
        )}
      </Router>
      </ConfirmProvider>
      </ToastProvider>
      </NotificationProvider>
    </AuthProvider>
  );
}

export default App;
